import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FormInputField from '../FormInputField';
import Button, { buttonType } from '../../components/Button';
import Href from '../Href';
import Loader from '../Loader';
import MigrationAlert from './MigrationAlert.jsx';
import { useLabels } from '../../hooks';
import { UserContext, UserType } from '../../contexts';
import { userApi } from '../../api';
import { env } from '../../config';
import styles from './LoginForm.module.scss';

const Login = ({ setViewState = () => { }, setNewPasswordRequiredData = () => { } }) => {
    const [showLoader, setShowLoader] = useState(false);
    const [error, setError] = useState();
    const { getLabel, getLocalUrlByLanguage, getCognitoApiError } = useLabels();
    const { signIn, resendConfirmationAccountCode, exchangeCodeForTokens, hasPermissions, userType } = useContext(UserContext);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const isActive = useRef(false);
    const loginUsernameRef = useRef();
    const loginPasswordRef = useRef();
    const migrationAlertRef = useRef();
    // Persist old-user context for migration
    const [oldUser, setOldUser] = useState(null);
    const [oldPassword, setOldPassword] = useState('');
    const [oldUserEmail, setOldUserEmail] = useState('');

    const processedCodesRef = useRef(new Set());
    const [awaitingPermissionsAfterOAuth, setAwaitingPermissionsAfterOAuth] = useState(false);

    useEffect(() => {
        isActive.current = true;

        return () => {
            isActive.current = false;
        };
    }, []);

    useEffect(() => {
        if (!env.IS_EDU_SIDE) {
            return;
        }

        const code = searchParams.get('code');

        if (code && isActive.current && !processedCodesRef.current.has(code)) {
            processedCodesRef.current.add(code);
            
            setError(null);
            setShowLoader(true);
            setAwaitingPermissionsAfterOAuth(true);

            exchangeCodeForTokens(code)
                .then(() => {
                    if (!isActive.current) return;
 
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.delete('code');
                    setSearchParams(newSearchParams, { replace: true });
                })
                .catch((error) => {
                    if (!isActive.current) return;
                    console.error('OAuth code exchange error:', error);
                    setError({ message: error.message || 'Failed to complete login. Please try again.' });
                    setShowLoader(false);
                    setAwaitingPermissionsAfterOAuth(false);
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.delete('code');
                    setSearchParams(newSearchParams, { replace: true });
                    processedCodesRef.current.delete(code);
                });
        }
    }, [searchParams, setSearchParams, exchangeCodeForTokens]);

    useEffect(() => {
        if (!env.IS_EDU_SIDE || !awaitingPermissionsAfterOAuth) {
            return;
        }

        if (hasPermissions) {
            setShowLoader(false);
            setAwaitingPermissionsAfterOAuth(false);
            return;
        }

        if (!userType) return;

        setShowLoader(false);
        setAwaitingPermissionsAfterOAuth(false);
        setError({ message: getLabel(
            userType === UserType.edu
                ? 'login_form_edu_school_not_registered'
                : 'login_form_edu_unexpected_error'
        ) });

    }, [awaitingPermissionsAfterOAuth, env.IS_EDU_SIDE, hasPermissions, userType, getLabel]);

    const onLogInButtonClickHandle = useCallback((event) => {
        event.preventDefault();
        let focusField = null;

        if (!loginUsernameRef.current.validation()) {
            focusField = focusField || loginUsernameRef.current;
        }

        if (!loginPasswordRef.current.validation()) {
            focusField = focusField || loginPasswordRef.current;
        }

        if (focusField) {
            focusField.focus();
            return;
        }

        setError(null);
        setShowLoader(true);

        const username = loginUsernameRef.current.getValue();
        const password = loginPasswordRef.current.getValue();
        signIn(username, password).then(() => {
            try { sessionStorage.removeItem('migrationInProgress'); } catch (_) {}
        }).catch(error => {
            if (isActive.current) {
                switch (error.code) {
                    case 'HandleNewPasswordRequired':
                        setNewPasswordRequiredData(error);
                        setViewState('new_password_required');
                        setShowLoader(false);
                        break;
                    case 'PasswordResetRequiredException':
                        setViewState('reset_password');
                        setShowLoader(false);
                        break;
                    case 'UserNotConfirmedException':
                        resendConfirmationAccountCode(username, password).finally(() => {
                            if (!isActive.current) return;
                            setShowLoader(false);
                            let shouldOpenMigration = false;
                            try { shouldOpenMigration = sessionStorage.getItem('migrationInProgress') === '1'; } catch (_) {}
                            if (shouldOpenMigration && migrationAlertRef.current && migrationAlertRef.current.openConfirm) {
                                migrationAlertRef.current.openConfirm(username, password);
                            } else {
                                navigate(getLocalUrlByLanguage(`/purchase`));
                            }
                        });
                        return;
                    default:
                        // Any Cognito error: try old DB; if that fails, show original Cognito error
                        checkOldUser(username, password, error);
                        break;
                }
            }
        });
    }, [signIn, setNewPasswordRequiredData, setViewState, resendConfirmationAccountCode, navigate, getLocalUrlByLanguage, getLabel]);

    const checkOldUser = useCallback((email, password, fallbackError) => {
        userApi.CheckUserInOldDB(email, password).then((response) => {
            if (isActive.current) {
                if (response && (response === true || response.exists === true)) {
                    if (response.alreadyMigrated) {
                        setError({ message: getLabel('migration_already_moved_message') });
                        setShowLoader(false);
                        return;
                    }
                    setOldUser(null);
                    setOldPassword(password);
                    setOldUserEmail(email);
                    if (migrationAlertRef.current) {
                        migrationAlertRef.current.open(email, password, null);
                    }
                    setShowLoader(false);
                } else {
                    // Old DB not found/invalid → surface original Cognito error if provided
                    if (fallbackError) {
                        setError(fallbackError);
                    } else {
                        setError({ message: typeof response === 'string' ? response : getLabel('login_error_invalid_credentials') });
                    }
                    setShowLoader(false);
                }
            }
        }).catch(() => {
            if (isActive.current) {
                // API failed → show original Cognito error if available
                if (fallbackError) {
                    setError(fallbackError);
                } else {
                    setError({ message: getLabel('login_error_invalid_credentials') });
                }
                setShowLoader(false);
            }
        });
    }, [getLabel]);

    const onSignUpButtonClickHandle = useCallback(() => {
        navigate(getLocalUrlByLanguage(`/purchase`));
    }, [getLocalUrlByLanguage, navigate]);

    const onSignUpWithOrgcodeButtonClickHandle = useCallback(() => {
        setViewState('set_org');
    }, [setViewState]);
    
    const onForgotPasswordButtonClickHandle = useCallback(() => {
        setViewState('forgot_password');
        setError(null);
    }, [setViewState]);

    const onEduLogInClickHandle = useCallback((event) => {
        if (!env.IS_EDU_SIDE) {
            window.location.href = getLabel('edu_site');
            return;
        }

        const googleLoginUrl = new URL(`https://${env.REACT_APP_COGNITO_DOMAIN}/oauth2/authorize`);
        googleLoginUrl.searchParams.append('response_type', 'code');
        googleLoginUrl.searchParams.append('client_id', env.REACT_APP_USER_POOL_CLIENT_ID);
        googleLoginUrl.searchParams.append('redirect_uri', `${window.location.origin}/login`);
        googleLoginUrl.searchParams.append('identity_provider', 'MOE-OIDC');
        googleLoginUrl.searchParams.append('scope', 'openid');

        window.location.href = googleLoginUrl.toString();
    }, [getLabel]);

    const loginForm = useMemo(() => {
        if (env.IS_EDU_SIDE) {
            return <>
                <div className={styles.login}>
                    <div className={styles.content}>
                        <div>
                            <div className={styles.title}>{getLabel('login_form_title')}</div>
                            <Href className={`${styles.org_button} ${styles.edu_button}`} onClick={onEduLogInClickHandle}>{getLabel('login_form_edu_button')}</Href>
                            <div className={styles.error} dangerouslySetInnerHTML={{ __html: getCognitoApiError(error)}} />
                        </div>
                    </div>
                </div>
                {showLoader && <Loader />}
            </>;
        }

        return <>
            <div className={styles.login}>
                <div className={styles.content}>
                    <div>
                        <div className={styles.title}>{getLabel('login_form_title')}</div>
                        <form className={styles.fields} onSubmit={onLogInButtonClickHandle}>
                            <FormInputField label={getLabel(env.REACT_APP_ORG_ENABLE() ? 'login_form_username_org_field' : 'login_form_username_field')} placeholder={getLabel(env.REACT_APP_ORG_ENABLE() ? 'yourname@yourcompany.com' : 'login_form_username_field')} ref={loginUsernameRef} type="email" required={true}
                                heContentDirection={'ltr'} hePlaceholderDirection={(env.REACT_APP_ORG_ENABLE() ? 'ltr' : 'rtl')} />
                            <FormInputField label={getLabel('login_form_password_field')} placeholder={getLabel('login_form_password_field')} ref={loginPasswordRef} type="password" required={true} heContentDirection={'ltr'} hePlaceholderDirection={'rtl'} />
                            <button hidden={true}></button>
                        </form>
                        <div className={styles.forgot_password}>
                            <Button type={buttonType.link} className={styles.forgot_password_button} onClick={onForgotPasswordButtonClickHandle} >{getLabel('login_form_forgot_password_button')}</Button>
                        </div>
                    </div>
                    <div className={styles.buttons}>
                        <div>
                            <div className={styles.error} dangerouslySetInnerHTML={{ __html: getCognitoApiError(error)}} />
                            <Button type={buttonType.premium} className={styles.main_button} onClick={onLogInButtonClickHandle} >{getLabel('login_form_login_button')}</Button>
                        </div>
                        {!env.REACT_APP_ORG_ENABLE() && <Button type={buttonType.link} className={styles.sign_up_button} onClick={onSignUpButtonClickHandle} >{getLabel('login_form_signup_button')}</Button>}
                        {env.REACT_APP_ORG_ENABLE() && <Button type={buttonType.link} className={styles.sign_up_button} onClick={onSignUpWithOrgcodeButtonClickHandle} >{getLabel('login_form_signup_with_orgcode_button')}</Button>}
                    </div>
                    <div className={styles.org}>
                        {!env.REACT_APP_ORG_ENABLE() && <>
                            <Href className={`${styles.org_button} ${styles.edu_button}`} onClick={onEduLogInClickHandle}>{getLabel('login_form_edu_button')}</Href>
                            <Href className={styles.org_button} to={getLabel('org_site')}>{getLabel('login_form_org_button')}</Href>
                        </>}
                        {env.REACT_APP_ORG_ENABLE() && <>
                            <Href className={styles.org_button} to={getLabel('main_site')}>{getLabel('login_form_main_site_button')}</Href>
                        </>}
                    </div>
                </div>
            </div>
            {showLoader && <Loader />}
            {<MigrationAlert oldPassword={oldPassword} oldUserEmail={oldUserEmail} oldUser={oldUser} ref={migrationAlertRef} />}
        </>;
    }, [
        error, 
        getCognitoApiError, 
        getLabel, 
        onForgotPasswordButtonClickHandle, 
        onLogInButtonClickHandle, 
        onSignUpButtonClickHandle, 
        onSignUpWithOrgcodeButtonClickHandle, 
        showLoader, 
        onEduLogInClickHandle,
    ]);

    return loginForm;
};

export default Login;